import { ref, reactive, computed } from 'vue'

/**
 * 表单验证Hook
 */
export function useValidation() {

  const errors = reactive({})
  const isValid = ref(true)
  const isDirty = ref(false)

  // 验证规则
  const rules = reactive({
    title: [
      { required: true, message: '请输入日程标题', trigger: 'blur' },
      { min: 2, max: 200, message: '标题长度为2-200字符', trigger: 'blur' }
    ],
    startTime: [
      { required: true, message: '请选择开始时间', trigger: 'change' }
    ],
    endTime: [
      {
        validator: (rule, value, callback, form) => {
          if (!value) {
            callback(new Error('请选择结束时间'))
          } else if (form.startTime && value <= form.startTime) {
            callback(new Error('结束时间必须大于开始时间'))
          } else {
            callback()
          }
        },
        trigger: 'change'
      }
    ],
    attendees: [
      {
        validator: (rule, value, callback) => {
          if (value && value.length > 0) {
            // 验证每个参与人
            const invalid = value.filter(a => !a.id)
            if (invalid.length > 0) {
              callback(new Error('参与人信息不完整'))
            }
          }
          callback()
        },
        trigger: 'change'
      }
    ]
  })

  // 必填验证
  function required(message = '此项为必填') {
    return {
      required: true,
      message,
      trigger: 'blur'
    }
  }

  // 最小长度
  function minLength(min, message) {
    return {
      validator: (rule, value, callback) => {
        if (value && value.length < min) {
          callback(message || `最少${min}个字符`)
        }
        callback()
      },
      trigger: 'blur'
    }
  }

  // 最大长度
  function maxLength(max, message) {
    return {
      validator: (rule, value, callback) => {
        if (value && value.length > max) {
          callback(message || `最多${max}个字符`)
        }
        callback()
      },
      trigger: 'blur'
    }
  }

  // 自定义验证
  function custom(validator) {
    return {
      validator,
      trigger: 'blur'
    }
  }

  // 时间比较验证
  function timeCompare(field) {
    return {
      validator: (rule, value, _callback, form) => {
        if (value && form[field] && value <= form[field]) {
          return `${field === 'endTime' ? '结束' : '开始'}时间必须晚于${field === 'endTime' ? '开始' : '结束'}时间`
        }
      },
      trigger: 'change'
    }
  }

  // 验证单个字段
  function validateField(field, form) {
    const fieldRules = rules[field]
    if (!fieldRules) return true

    delete errors[field]

    for (const rule of fieldRules) {
      const value = form[field]

      // 必填验证
      if (rule.required) {
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          errors[field] = rule.message
          return false
        }
      }

      // 长度验证
      if (rule.min !== undefined && value && value.length < rule.min) {
        errors[field] = rule.message || `最少${rule.min}个字符`
        return false
      }

      if (rule.max !== undefined && value && value.length > rule.max) {
        errors[field] = rule.message || `最多${rule.max}个字符`
        return false
      }

      // 自定义验证
      if (rule.validator) {
        const result = rule.validator(rule, value, () => {}, form)
        if (result instanceof Promise) {
          // 异步验证
        } else if (result !== undefined && result !== true) {
          errors[field] = result
          return false
        }
      }
    }

    return true
  }

  // 验证整个表单
  function validate(form) {
    isDirty.value = true
    let valid = true

    for (const field in rules) {
      if (!validateField(field, form)) {
        valid = false
      }
    }

    isValid.value = valid
    return valid
  }

  // 清空验证
  function clearValidate() {
    for (const key in errors) {
      delete errors[key]
    }
    isValid.value = true
    isDirty.value = false
  }

  // 重置表单
  function reset(form) {
    clearValidate()
    for (const key in form) {
      if (Array.isArray(form[key])) {
        form[key] = []
      } else if (typeof form[key] === 'object' && form[key] !== null) {
        form[key] = {}
      } else {
        form[key] = undefined
      }
    }
  }

  // 获取错误消息
  function getError(field) {
    return errors[field]
  }

  // 是否有错误
  function hasError(field) {
    return !!errors[field]
  }

  // 暴露方法
  return {
    errors: computed(() => errors),
    isValid: computed(() => isValid.value),
    isDirty: computed(() => isDirty.value),
    rules,
    validate,
    validateField,
    clearValidate,
    reset,
    getError,
    hasError,
    required,
    minLength,
    maxLength,
    custom,
    timeCompare
  }
}

/**
 * 冲突检测Hook
 */
export function useConflictCheck(options = {}) {
  const {
    checkApi
  } = options

  const conflicts = ref([])
  const isChecking = ref(false)

  // 检测时间冲突
  async function checkTimeConflict(params) {
    if (!checkApi) return []

    isChecking.value = true
    try {
      const result = await checkApi(params)
      conflicts.value = result
      return result
    } catch (error) {
      console.error('Conflict check failed:', error)
      return []
    } finally {
      isChecking.value = false
    }
  }

  // 清除冲突
  function clearConflicts() {
    conflicts.value = []
  }

  return {
    conflicts: computed(() => conflicts.value),
    isChecking: computed(() => isChecking.value),
    checkTimeConflict,
    clearConflicts
  }
}

/**
 * 加载状态Hook
 */
export function useLoading(options = {}) {
  const {
    defaultText = '加载中...'
  } = options

  const loading = ref(false)
  const loadingText = ref(defaultText)

  // 开始加载
  function start(text) {
    loading.value = true
    loadingText.value = text || defaultText
  }

  // 结束加载
  function stop() {
    loading.value = false
  }

  // 包装异步操作
  async function wrap(promise, loadingTextValue) {
    start(loadingTextValue)
    try {
      return await promise
    } finally {
      stop()
    }
  }

  return {
    loading: computed(() => loading.value),
    loadingText: computed(() => loadingText.value),
    start,
    stop,
    wrap
  }
}

/**
 * 消息提示Hook
 */
export function useMessage() {
  // 成功提示
  function success(message) {
    // 可以替换为 Element Plus 的 ElMessage
    if (typeof window !== 'undefined' && window.$message) {
      window.$message.success(message)
    }
  }

  // 错误提示
  function error(message) {
    console.error('[Error]', message)
    if (typeof window !== 'undefined' && window.$message) {
      window.$message.error(message)
    }
  }

  // 警告提示
  function warning(message) {
    console.warn('[Warning]', message)
    if (typeof window !== 'undefined' && window.$message) {
      window.$message.warning(message)
    }
  }

  // 确认对话框
  function confirm(options) {
    const { title = '确认', message, onConfirm, onCancel } = options

    if (typeof window !== 'undefined' && window.$confirm) {
      return window.$confirm(message, title, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(onConfirm).catch(onCancel || (() => {}))
    }

    // 降级处理
    if (confirm(message)) {
      onConfirm?.()
    } else {
      onCancel?.()
    }
  }

  return {
    success,
    error,
    warning,
    confirm
  }
}
